import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Rss, MessageCircle, Radio, X } from 'lucide-react';

/**
 * SignalSourcesTeaser
 * Dismissable pointer to the shipped Signal Sources page (Reddit, HN, RSS).
 * Shown on the dashboard when no workspace is selected yet.
 */
const SOURCES = [
  { name: 'Reddit',       icon: MessageCircle, hint: 'r/SaaS, r/indiehackers' },
  { name: 'Hacker News',  icon: Radio,          hint: 'Show HN, Ask HN mentions' },
  { name: 'RSS / Blogs',  icon: Rss,            hint: 'Any feed you already read' },
];

const STORAGE_KEY = 'vibesprint_signal_sources_teaser_dismissed';

export const SignalSourcesTeaser = ({ onDismiss }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const wasDismissed = localStorage.getItem(STORAGE_KEY);
    if (!wasDismissed) setVisible(true);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    if (onDismiss) onDismiss();
  };

  if (!visible) return null;

  return (
    <Card
      className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-primary/5 via-background to-background"
      data-testid="signal-sources-teaser"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative p-5 sm:p-6">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-3 right-3 rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label="Dismiss Signal Sources teaser"
          data-testid="signal-sources-teaser-dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge
                variant="outline"
                className="border-primary/40 text-primary bg-primary/5"
                data-testid="signal-sources-teaser-badge"
              >
                Beta
              </Badge>
            </div>

            <h3
              className="text-lg sm:text-xl font-semibold tracking-tight mb-1"
              data-testid="signal-sources-teaser-title"
            >
              Stop pasting feedback. Let signals come to you.
            </h3>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Connect Reddit, Hacker News and any RSS feed once — VibeSprint auto-fetches new
              signals and drops them straight into Build / Fix / Ignore. No copy-paste. No import
              button.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {SOURCES.map((src) => {
                const Icon = src.icon;
                return (
                  <div
                    key={src.name}
                    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md border bg-card text-xs"
                    data-testid={`signal-source-chip-${src.name.toLowerCase().replace(/\s|\//g, '-')}`}
                  >
                    <Icon className="h-3.5 w-3.5 text-primary" />
                    <span className="font-medium">{src.name}</span>
                    <span className="text-muted-foreground hidden sm:inline">· {src.hint}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:w-[180px] flex-shrink-0">
            <Button
              size="sm"
              onClick={() => navigate('/sources')}
              className="btn-cta w-full"
              data-testid="signal-sources-teaser-open"
            >
              Open Signal Sources
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="w-full text-muted-foreground"
              data-testid="signal-sources-teaser-later"
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SignalSourcesTeaser;
